import { type PerkIds, Perks } from "./items/perks";

/**
 * The curated ruleset exposed by ordinary battlefield loot.
 *
 * The inherited registries remain available for protocol compatibility and
 * staged reintroduction. Partner-map special containers are documented
 * exceptions and ordinary ground/crate loot must be sourced from this list.
 *
 * Roster revision 2 (owner request): Service Pistol, Trench Carbine, Breach Gun
 * and Long Rifle retired; PSM, OTs-23, Micro Uzi, AKs-74u, MCX Spear, ACR, MG5,
 * Stevens 555, SSG 82, Firework Launcher and G17 (scoped) added. Retired guns are
 * also swapped out of inherited containers (see RETIRED_REPLACEMENTS in lootTables).
 *
 * Roster revision 3 (owner request, October 2026): the Mini-14 retired for the
 * Vector (its place in tier 3, its wall mount in the Fargo headquarters), and the
 * PP-19 Bizon added to tier 3. The OTs-23 is shown as the Swish Cheese (a pistol
 * with a switch); its id stays.
 *
 * Later (owner request, 12 October 2026): the Vector 2.5 times rarer wherever it
 * drops (gunRarity below), and its damage 9 → 8.5 a bullet.
 */
const gunTiers = Object.freeze({
    tier1: ["m1895", "mp5k", "psm", "stevens_555", "g17_scoped"] as const,
    tier2: ["aug", "mpx", "sks", "m3k", "aks74u", "ots23", "micro_uzi"] as const,
    tier3: ["fn_fal", "vector", "mg36", "cz600", "mcx_spear", "acr", "mg5", "ssg82", "firework_launcher", "pp19"] as const
});

/** novelty guns: rarer inside their tier, and not handed to development bots */
const novelty = ["g17_scoped", "firework_launcher"] as const;

export const ActiveRuleset = Object.freeze({
    gunTiers,
    guns: [...gunTiers.tier1, ...gunTiers.tier2, ...gunTiers.tier3] as const,
    novelty,
    /** relative weight of a gun inside its tier (default 1) */
    gunWeights: Object.freeze({ g17_scoped: 0.35, firework_launcher: 0.35 }) as Readonly<Record<string, number>>,
    /**
     * how many times rarer a gun is than its weights say, everywhere it drops in normal mode: in every loot roll that
     * can give it (its tier, and every inherited container: briefcases, lockers, crates...) its chance is divided by
     * this, the roll's other outcomes sharing the difference as before (`applyGunRarity` in server lootTables.ts), and
     * its wall mounts are picked this much less often, the wall left bare instead (`rarerMount` in buildings.ts)
     */
    gunRarity: Object.freeze({ vector: 2.5 }) as Readonly<Record<string, number>>,
    /**
     * the Gravity Gloves grab, throw and merge boxes, crates and barrels (see definitions/gravity.ts); the Octo Gadget
     * (1.10) is a harness of four mechanical arms, a grapple hook that strikes (see definitions/octo.ts)
     */
    melee: ["entrenching_tool", "crowbar_halloween", "pan", "scythe", "chainsaw", "gas_can", "gravity_gloves", "octo_gadget"] as const,
    /** relative weight of a melee weapon in the melee table (default 1) */
    meleeWeights: Object.freeze({ entrenching_tool: 1, crowbar_halloween: 0.7, pan: 0.7, scythe: 0.5, chainsaw: 0.3, gas_can: 0.45, gravity_gloves: 0.68, octo_gadget: 0.55 }) as Readonly<Record<string, number>>,
    /** frags, smokes (smoke also puts fire out), C4 and the Molotov */
    throwables: ["frag_grenade", "smoke_grenade", "c4", "molotov"] as const,
    /** relative weight of a throwable in the curated throwables table (default 1) */
    throwableWeights: Object.freeze({ frag_grenade: 1, smoke_grenade: 0.8, c4: 0.5, molotov: 0.7 }) as Readonly<Record<string, number>>,
    healing: "gauze" as const,
    /** healing items in ground loot and crates, with their weights (Field Meat only comes from hares) */
    healingLoot: ["gauze", "cola", "medikit"] as const,
    healingWeights: Object.freeze({ gauze: 3, cola: 2, medikit: 0.7 }) as Readonly<Record<string, number>>,
    ammo: ["9mm", "762mm", "12g", "556mm", "545mm", "firework_rocket"] as const,
    helmets: ["basic_helmet", "tactical_helmet"] as const,
    vests: ["basic_vest", "tactical_vest"] as const,
    backpacks: ["basic_pack", "tactical_pack"] as const,
    /**
     * items taken out of loot, each with what takes its place in normal mode: in every loot roll (the scope tables and
     * airdrops: `curate` in server lootTables.ts) and a building's view from inside (the hunting stand's `ceilingScope`:
     * server player.ts); the Training Range's racks leave them out. Owner requests of 16 October 2026: the 8x scope, then
     * the 16x, give way to the 4x. The sniper rifles come with a 6x in their place (`gunScopes`), and the 6x is rare loot
     * of its own (a fifth as often as the 4x in the strong crates' scope roll, `special_scopes` in lootTables.ts)
     */
    retiredItems: Object.freeze({ "8x_scope": "4x_scope", "16x_scope": "4x_scope" }) as Readonly<Record<string, string>>,
    /**
     * the scope a gun comes with (its `spawnScope`: server lootHelpers.ts), as the game gives it in normal mode. The 16x
     * is out of the game (owner request, 16 October 2026), and so is the 8x: the 6x takes its place (the owner, the same
     * day: "Add a x6 scope to replace the 8x"). The sniper rifles come with the 6x: the Tango 51 and the VKS (which had
     * the 8x), the L115A1 and the ULR-338 (which had the 16x)
     */
    gunScopes: Object.freeze({ "8x_scope": "6x_scope", "16x_scope": "6x_scope" }) as Readonly<Record<string, string>>,
    /**
     * items taken out of the game with nothing in their place, in normal mode: a loot roll that gave one gives nothing
     * (`curate` in server lootTables.ts) and a player is never given one (`Player.addPerk`). The Costumed perk (owner
     * request, 16 October 2026): it disguised its player as a random tree, rock, crate or barrel (the jack-o'-lanterns'
     * loot, and Plumpkin Gamble's draw). The 1x scope (the owner, the same day: "since we spawn with 2x, might aswell
     * just remove the 1x"): nobody starts with it any more, and the drawers, bookshelves, boxes, desks, toilets,
     * dumpsters and campsite crates that rolled one (the inherited `scopes` table) give nothing in its place, as they
     * effectively did while everyone carried a 1x and couldn't pick up another. It stays the closest view
     * (`DEFAULT_SCOPE`: inside buildings, downed, in smoke)
     */
    removedItems: Object.freeze(["costumed", "1x_scope"]) as readonly string[],
    /**
     * no perks at all (owner request, 20 October 2026: "Remove all perks by the way"; none could be found on Fargo
     * already, 0.85): in normal mode every perk counts as a removed item (`isRemovedItem`), so a loot roll that gave one
     * gives nothing (`curate` in server lootTables.ts), a player is never given one, however it would come (`Player.addPerk`:
     * a perk lying on the ground, power gear's perk, anything else), and the HUD has no perk slots (client nightCommand.scss).
     * The definitions stay (protocol compatibility and staged restoration rely on them: AGENTS.md)
     */
    noPerks: true as boolean
});

export type ActiveGunId = typeof ActiveRuleset.guns[number];

/** an item as the game gives it in normal mode: a retired one (`ActiveRuleset.retiredItems`) is swapped for its replacement */
export const activeItemId = <T extends string>(id: T): T =>
    (Object.hasOwn(ActiveRuleset.retiredItems, id) ? ActiveRuleset.retiredItems[id] : id) as T;

/** the scope a gun comes with in normal mode (`ActiveRuleset.gunScopes`: the sniper rifles' 8x or 16x becomes a 6x) */
export const activeGunScope = <T extends string>(id: T): T =>
    (Object.hasOwn(ActiveRuleset.gunScopes, id) ? ActiveRuleset.gunScopes[id] : id) as T;

/** an item the game no longer gives at all in normal mode (`ActiveRuleset.removedItems`, and every perk: `noPerks`) */
export const isRemovedItem = (id: string): boolean =>
    ActiveRuleset.removedItems.includes(id) || (ActiveRuleset.noPerks && Perks.fromStringSafe(id as PerkIds) !== undefined);
